import { NOTE_EDIT_MAX_STEPS, type NoteEditOrigin } from "@rosc/contracts";
import { useEffect, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import type { NoteAssistantTarget } from "@/common/types/noteAssistant";
import { ToastAction } from "@/components/ui/toast";
import { toast } from "@/hooks/use-toast";
import { useMacroShortcuts } from "@/hooks/visits/useMacroShortcuts";
import {
  type NoteProposal,
  type PreChartAddRequest,
  type ProposeOutcome,
  useNoteAssistant,
} from "@/hooks/visits/useNoteAssistant";
import {
  type RestoreOutcome,
  useNoteRestore,
} from "@/hooks/visits/useNoteRestore";
import { readUndoneFiles, writeUndoneFiles } from "@/lib/pre-chart";

const NO_SOURCES: readonly number[] = [];

// An instruction sent while another is being proposed or applied: it is
// proposed next, refining the proposal once it is ready, or on the note as
// it is then (clinical lead, 30/10 and 31/10).
export interface QueuedRequest {
  id: number;
  instruction: string;
  scope?: RequestScope;
}

// What a request may read besides the note, when it is not what the Ask bar
// uses (every file in use, and a pre-chart's lab choice). A pre-chart's new
// file reads only that file; a template change reads no file and takes no
// lab choice: it only moves what the draft already has (staging check
// 03/11: the rewrite had dropped normal lab values and added values from a
// file whose add was undone).
export interface RequestScope {
  sourceIds: readonly number[];
  preChartLabs: boolean;
  // Pre-chart drafts: how the files are added (Spread into sections, or
  // Summarize and insert under a heading).
  preChartAdd?: PreChartAddRequest;
}

// An automatic edit (a pre-chart's file, from the clinician's button): it
// applies itself with Undo. Its label names the files; a summary also has
// the heading it goes under.
export interface AutoEdit {
  label: string;
  kind: PreChartAddRequest["mode"];
  heading?: string;
}

// An earlier request of the proposal under review, and the proposal it
// produced (shown again when a refinement fails or is stopped).
export interface ProposalStep {
  instruction: string;
  proposal: NoteProposal;
}

// An edit applied from the assistant, kept so it can be undone: the note
// before and after it. A refined proposal is one edit (its requests one per
// line).
export interface AppliedEdit {
  id: number;
  instruction: string;
  before: string;
  after: string;
  target: NoteAssistantTarget;
  undone: boolean;
  // A pre-chart's files added (Spread into sections, Summarize and insert,
  // Fill from attached files): the files it read.
  addedSources?: readonly number[];
}

// Each pre-chart file that was added: "added" while an edit that added it
// stands, else "undone". "Fill from attached files" leaves undone files
// out, the tray marks them, and the hint offers them again.
export const sourceAddStates = (
  edits: readonly AppliedEdit[],
): ReadonlyMap<number, "added" | "undone"> => {
  const states = new Map<number, "added" | "undone">();
  for (const edit of edits)
    for (const id of edit.addedSources ?? [])
      if (!edit.undone) states.set(id, "added");
      else if (!states.has(id)) states.set(id, "undone");
  return states;
};
const withState = (
  states: ReadonlyMap<number, "added" | "undone">,
  state: "added" | "undone",
): ReadonlySet<number> =>
  new Set([...states].filter(([, value]) => value === state).map(([id]) => id));

// "Insert <shortcut>" after what is already typed, as iOS does.
export const withShortcut = (
  draft: string,
  name: string,
  insert: (name: string) => string,
): string => {
  const base = draft.trim();
  const phrase = insert(name.trim());
  if (!base) return phrase;
  return `${base}${base.endsWith(".") ? " " : ". "}${phrase}`;
};

// The v3.2 Ask bar's state, shared by the bar under the note and the one in
// the assistant panel: the draft being typed, the instruction that was sent
// (the proposal belongs to it) and the same propose / apply calls as the
// classic panel (useNoteAssistant).
export const useAskAssistant = ({
  target,
  sourceIds = NO_SOURCES,
  onApplied,
  preChartDraft = false,
}: {
  target: NoteAssistantTarget;
  sourceIds?: readonly number[];
  onApplied: () => void;
  // A pre-chart draft (its lab values go with each request).
  preChartDraft?: boolean;
}) => {
  const { t } = useTranslation();
  const [draft, setDraft] = useState("");
  const [sent, setSent] = useState("");
  const [sends, setSends] = useState(0);
  // A prepared request (a transcript correction's note update): where it
  // came from, so the fact check asks about every changed fact.
  const [origin, setOrigin] = useState<{
    origin: NoteEditOrigin;
    changes: number;
    omitted: number;
  } | null>(null);
  // A request sent while a proposal is under review refines it (clinical
  // lead, 31/10: "remove medication history", then "remove the header", then
  // apply once): the earlier requests of that proposal, oldest first.
  const [chain, setChain] = useState<ProposalStep[]>([]);
  // The sent request's own scope (null: the Ask bar's files and labs). A
  // refinement keeps the scope of the proposal it refines.
  const [scope, setScope] = useState<RequestScope | null>(null);
  // A request reads the files in use when it starts. A file that lands
  // while it is written does not restart it (staging check 03/11: files
  // uploaded together restarted the add of the earlier ones, and only the
  // last file was read).
  const liveSourceIds = useRef(sourceIds);
  liveSourceIds.current = sourceIds;
  const frozen = (next: RequestScope | null): RequestScope =>
    next ?? { sourceIds: [...liveSourceIds.current], preChartLabs: true };
  const last = chain.at(-1);
  const refine = useMemo(
    () =>
      last
        ? {
            proposal: last.proposal,
            instructions: chain.map((step) => step.instruction),
          }
        : undefined,
    [chain, last],
  );
  const assistant = useNoteAssistant(
    target,
    sent,
    scope ? scope.sourceIds : sourceIds,
    origin?.origin,
    refine,
    preChartDraft && (scope ? scope.preChartLabs : true),
    scope?.preChartAdd,
  );
  const propose = useRef(assistant.propose);
  propose.current = assistant.propose;
  const show = useRef(assistant.show);
  show.current = assistant.show;
  const [queue, setQueue] = useState<QueuedRequest[]>([]);
  const [applied, setApplied] = useState<AppliedEdit[]>([]);
  const [undoing, setUndoing] = useState(false);
  // Pre-chart drafts: files whose add was undone, kept across reloads
  // (readUndoneFiles); an add in this session takes a file off the list.
  const visitPublicId = target.visitPublicId;
  const [storedUndone, setStoredUndone] = useState<ReadonlySet<number>>(() =>
    preChartDraft ? readUndoneFiles(visitPublicId) : new Set(),
  );
  useEffect(() => {
    setStoredUndone(preChartDraft ? readUndoneFiles(visitPublicId) : new Set());
  }, [preChartDraft, visitPublicId]);
  const storeUndone = (ids: readonly number[], undone: boolean): void => {
    if (!preChartDraft || !ids.length) return;
    const next = new Set(readUndoneFiles(visitPublicId));
    for (const id of ids) {
      if (undone) next.add(id);
      else next.delete(id);
    }
    writeUndoneFiles(visitPublicId, next);
    setStoredUndone(next);
  };
  const nextId = useRef(0);
  const restoreNote = useNoteRestore();
  // Another document: its own requests and edits.
  const targetKey = `${target.visitPublicId}:${target.notePublicId ?? ""}`;
  // biome-ignore lint/correctness/useExhaustiveDependencies: resets per document.
  useEffect(() => {
    setQueue([]);
    setApplied([]);
    setChain([]);
    setScope(null);
    starting.current = false;
  }, [targetKey]);
  const active =
    assistant.proposing ||
    assistant.applying ||
    !!assistant.result ||
    assistant.proposeFailed;
  // Requests can be added to a proposal under review: not to a transcript
  // correction's update (it is proposed and confirmed on its own), and up to
  // NOTE_EDIT_MAX_STEPS requests in one proposal.
  const refinable = !origin && chain.length + 1 < NOTE_EDIT_MAX_STEPS;
  const canRefine =
    !!assistant.result && !assistant.applying && refinable && !!sent;
  // Set when an instruction starts, until the assistant shows it is busy
  // with it (the start and the proposal are a render apart).
  const starting = useRef(false);
  useEffect(() => {
    if (active) starting.current = false;
  }, [active]);

  // The saved note changed under a proposal being refined: as when Apply
  // finds it changed, the proposal is dropped and the clinician asks again.
  const onProposed = (outcome: ProposeOutcome | null): void => {
    if (outcome !== "stale") return;
    setChain([]);
    setSent("");
    toast({ variant: "error", title: t("noteAssistant.staleError") });
  };
  const proposed = useRef(onProposed);
  proposed.current = onProposed;

  // Proposes once the sent instruction is the hook's (after the render
  // that set it).
  useEffect(() => {
    if (sends > 0)
      void propose.current().then((outcome) => proposed.current(outcome));
  }, [sends]);

  // Going back to the previous proposal: shown once its instruction is the
  // hook's again (after the render that set it).
  const [restoring, setRestoring] = useState<NoteProposal | null>(null);
  useEffect(() => {
    if (!restoring) return;
    show.current(restoring);
    setRestoring(null);
  }, [restoring]);

  const shortcuts = useMacroShortcuts();

  // An edit the workspace started on its own (a pre-chart's new file): it
  // is applied as soon as it is proposed, with Undo, unless it needs the
  // clinician's review (fact changes) or proposes nothing.
  const [auto, setAuto] = useState<AutoEdit | null>(null);
  // Requests shown in other words than sent (an automatic edit's), by the
  // text sent.
  const [shownFor, setShownFor] = useState<ReadonlyMap<string, string>>(
    () => new Map(),
  );

  const start = (
    instruction: string,
    next: RequestScope | null = null,
  ): void => {
    starting.current = true;
    setOrigin(null);
    setChain([]);
    setScope(frozen(next));
    setSent(instruction);
    setSends((n) => n + 1);
  };

  // The next request runs on the proposal under review; the result replaces
  // it as one proposal, still compared with the saved note.
  const refineWith = (instruction: string): void => {
    const proposal = assistant.result;
    if (!proposal) return;
    starting.current = true;
    setChain((steps) => [...steps, { instruction: sent, proposal }]);
    setSent(instruction);
    setSends((n) => n + 1);
  };

  // Sends at once when the assistant is free or a proposal is under review
  // (it refines that proposal); otherwise the instruction waits its turn. A
  // request with its own scope never refines another proposal: it waits
  // until that one is applied or discarded.
  const send = (text = draft, requestScope?: RequestScope): boolean => {
    const instruction = text.trim();
    if (!instruction || undoing) return false;
    setDraft("");
    const waiting =
      assistant.proposing ||
      assistant.applying ||
      assistant.proposeFailed ||
      queue.length > 0 ||
      starting.current ||
      (!!assistant.result && (!canRefine || !!requestScope));
    if (waiting) {
      nextId.current += 1;
      const id = nextId.current;
      setQueue((items) => [
        ...items,
        { id, instruction, ...(requestScope ? { scope: requestScope } : {}) },
      ]);
    } else if (assistant.result) refineWith(instruction);
    else start(instruction, requestScope ?? null);
    return true;
  };

  // Starts an edit that applies itself (see `auto`); false while the
  // assistant is busy, so the caller tries again once it is free.
  const sendAuto = (
    instruction: string,
    label: string,
    requestScope?: RequestScope,
  ): boolean => {
    const text = instruction.trim();
    if (
      !text ||
      undoing ||
      starting.current ||
      assistant.proposing ||
      assistant.applying ||
      assistant.proposeFailed ||
      assistant.result ||
      queue.length > 0
    )
      return false;
    const add = requestScope?.preChartAdd;
    if (!add) return false;
    // What the clinician sees for it: the button and the file names, never
    // the model's wording with its SOURCE numbers (staging check of #553).
    const shown =
      add.mode === "spread"
        ? t("preChart.fileAdd.spreadRequest", { name: label })
        : add.heading
          ? t("preChart.fileAdd.summaryRequest", {
              name: label,
              heading: add.heading,
            })
          : t("preChart.fileAdd.summaryRequestNoHeading", { name: label });
    setShownFor((map) => new Map(map).set(text, shown));
    setAuto({
      label,
      kind: add.mode,
      ...(add.mode === "summary" && add.heading
        ? { heading: add.heading }
        : {}),
    });
    start(text, requestScope ?? null);
    return true;
  };

  // The next waiting instruction goes once the proposal it waited for is
  // ready (it refines it), applied or discarded; not while a proposal
  // failed: Try again, Back or Discard decides.
  const head = queue[0];
  const hasResult = !!assistant.result;
  // biome-ignore lint/correctness/useExhaustiveDependencies: runs when the assistant frees up.
  useEffect(() => {
    if (
      !head ||
      undoing ||
      starting.current ||
      assistant.proposing ||
      assistant.applying ||
      assistant.proposeFailed ||
      (hasResult && (!canRefine || !!head.scope))
    )
      return;
    setQueue((items) => items.slice(1));
    if (hasResult) refineWith(head.instruction);
    else start(head.instruction, head.scope ?? null);
  }, [
    head,
    hasResult,
    canRefine,
    assistant.proposing,
    assistant.applying,
    assistant.proposeFailed,
    undoing,
  ]);

  const removeQueued = (id: number): void => {
    setQueue((items) => items.filter((item) => item.id !== id));
  };

  // Proposes a prepared instruction at once (e.g. "Update note" after a
  // transcript correction).
  const sendRequest = (request: {
    instruction: string;
    origin: NoteEditOrigin;
    changes: number;
    omitted: number;
  }): void => {
    assistant.clear();
    setChain([]);
    setScope(frozen(null));
    setOrigin({
      origin: request.origin,
      changes: request.changes,
      omitted: request.omitted,
    });
    setSent(request.instruction);
    setSends((n) => n + 1);
  };

  const retry = (): void => {
    if (sent) setSends((n) => n + 1);
  };

  // Stop while writing, or Discard a proposal with all its requests:
  // nothing is saved.
  const discard = (): void => {
    starting.current = false;
    assistant.clear();
    setChain([]);
    setSent("");
    setOrigin(null);
    setScope(null);
    setAuto(null);
  };

  // Drops the request that is refining the proposal (it failed, or was
  // stopped while written) and shows the proposal it was refining again.
  const back = (): void => {
    if (!last) return;
    starting.current = false;
    setChain((steps) => steps.slice(0, -1));
    setSent(last.instruction);
    setRestoring(last.proposal);
  };

  // Stop while writing: back to the proposal being refined, if any.
  const stop = (): void => {
    if (last) back();
    else discard();
  };

  // Undoes the last applied edit that is not undone yet: the note goes back
  // to how it was before it, while it is still the note the edit made.
  const undo = async (id?: number): Promise<RestoreOutcome | null> => {
    const last = [...applied].reverse().find((edit) => !edit.undone);
    if (!last || (id !== undefined && last.id !== id) || undoing) return null;
    setUndoing(true);
    const outcome = await restoreNote({
      visitPublicId: last.target.visitPublicId,
      notePublicId: last.target.notePublicId,
      restore: last.before,
      expected: last.after,
    });
    setUndoing(false);
    if (outcome === "restored") {
      setApplied((edits) =>
        edits.map((edit) =>
          edit.id === last.id ? { ...edit, undone: true } : edit,
        ),
      );
      storeUndone(last.addedSources ?? [], true);
      toast({ variant: "success", title: t("noteAssistant.undone") });
      onApplied();
    } else {
      toast({
        variant: "error",
        title:
          outcome === "stale"
            ? t("noteAssistant.undoStale")
            : t("noteAssistant.undoError"),
      });
    }
    return outcome;
  };
  const addStates = useMemo(() => sourceAddStates(applied), [applied]);
  const undoneSources = useMemo(() => {
    const ids = new Set(withState(addStates, "undone"));
    for (const id of storedUndone)
      if (addStates.get(id) !== "added") ids.add(id);
    return ids as ReadonlySet<number>;
  }, [addStates, storedUndone]);
  const addedSources = useMemo(
    () => withState(addStates, "added"),
    [addStates],
  );
  const undoRef = useRef(undo);
  undoRef.current = undo;

  const apply = async (
    acknowledged: boolean,
    appliedTitle = t("noteAssistant.appliedToast"),
    // The toast (with Undo) comes once the note shows the edit (staging
    // checks of #548 and #553: it came before the text landed).
    { waitForNote = true }: { waitForNote?: boolean } = {},
  ): Promise<void> => {
    const proposal = assistant.result;
    const instruction = [...chain.map((step) => step.instruction), sent].join(
      "\n",
    );
    // A pre-chart's files added: the files it read, for "undone" on its
    // Undo.
    const added =
      scope?.preChartAdd && scope.sourceIds.length
        ? [...scope.sourceIds]
        : undefined;

    const outcome = await assistant.apply(acknowledged, { waitForNote });
    if (!outcome || !assistant.isCurrent()) return;
    // Saved, or no longer the proposal to apply: its requests go with it.
    if (outcome === "saved" || outcome === "stale" || outcome === "uncertain")
      setChain([]);
    if (outcome === "saved") {
      setSent("");
      setOrigin(null);
      setScope(null);
      nextId.current += 1;
      const id = nextId.current;
      if (proposal)
        setApplied((edits) => [
          ...edits,
          {
            id,
            instruction,
            before: proposal.current,
            after: proposal.proposed,
            target: proposal.target,
            undone: false,
            ...(added ? { addedSources: added } : {}),
          },
        ]);
      if (added) storeUndone(added, false);
      toast({
        variant: "success",
        title: appliedTitle,
        action: proposal ? (
          <ToastAction
            altText={t("noteAssistant.undo")}
            data-testid="applied-undo"
            onClick={() => void undoRef.current(id)}
          >
            {t("noteAssistant.undo")}
          </ToastAction>
        ) : undefined,
      });
      onApplied();
    } else if (outcome === "stale") {
      toast({ variant: "error", title: t("noteAssistant.staleError") });
    } else if (outcome === "uncertain") {
      toast({ variant: "error", title: t("noteAssistant.applyUncertain") });
    } else if (outcome === "error") {
      toast({ variant: "error", title: t("noteAssistant.applyError") });
    }
  };

  const applyRef = useRef(apply);
  applyRef.current = apply;
  const autoApplying = useRef(false);
  const discardRef = useRef(discard);
  discardRef.current = discard;
  // The automatic edit: applied once proposed; a failure or an empty
  // proposal is said in a toast and dropped.
  const autoResult = auto ? assistant.result : null;
  const autoFailed = !!auto && assistant.proposeFailed;
  useEffect(() => {
    if (!auto) return;
    if (autoFailed) {
      setAuto(null);
      discardRef.current();
      toast({
        variant: "error",
        title: t(
          auto.kind === "spread"
            ? "preChart.fileAdd.spreadFailed"
            : "preChart.fileAdd.summaryFailed",
          { name: auto.label },
        ),
      });
      return;
    }
    if (!autoResult || autoApplying.current) return;
    if (autoResult.proposed.trim() === autoResult.current.trim()) {
      setAuto(null);
      discardRef.current();
      toast({ title: t("preChart.fileAdd.nothing", { name: auto.label }) });
      return;
    }
    // Fact changes to confirm: left for review ("Proposal ready").
    if (autoResult.blocked) {
      setAuto(null);
      return;
    }
    // "Adding …" stays until the edit is saved.
    autoApplying.current = true;
    void applyRef
      .current(
        false,
        auto.kind === "spread"
          ? t("preChart.fileAdd.spreadDone", { name: auto.label })
          : auto.heading
            ? t("preChart.fileAdd.summaryDone", {
                name: auto.label,
                heading: auto.heading,
              })
            : t("preChart.fileAdd.summaryDoneNoHeading", { name: auto.label }),
        { waitForNote: true },
      )
      .finally(() => {
        autoApplying.current = false;
        setAuto(null);
      });
  }, [auto, autoResult, autoFailed, t]);

  return {
    draft,
    setDraft,
    // A request as the clinician sees it (each line of a refined one).
    shown: (text: string): string =>
      text
        .split("\n")
        .map((line) => shownFor.get(line) ?? line)
        .join("\n"),
    // The automatic edit being written or applied ("Spreading <file>…").
    auto,
    sendAuto,
    sent,
    send,
    sendRequest,
    origin,
    retry,
    discard,
    back,
    stop,
    apply,
    // The earlier requests of the proposal under review (each refined the
    // one before), oldest first; empty for a single request.
    steps: chain.map((step) => step.instruction),
    // A request sent now refines the proposal under review.
    canRefine,
    // Waiting requests will refine the proposal once it is ready.
    queueRefines: refinable && !assistant.applying,
    queue,
    removeQueued,
    applied,
    undoneSources,
    addedSources,
    undo,
    undoing,
    // The edit Undo would take back (the last one not undone).
    undoable: [...applied].reverse().find((edit) => !edit.undone) ?? null,
    result: assistant.result,
    proposing: assistant.proposing,
    applying: assistant.applying,
    proposeFailed: assistant.proposeFailed,
    shortcuts,
    insertShortcut: (name: string) => {
      setDraft((current) =>
        withShortcut(current, name, (value) =>
          t("v3.ask.insertShortcut", { name: value }),
        ),
      );
    },
  };
};

export type AskAssistant = ReturnType<typeof useAskAssistant>;
